/*
  Booking a private fitting (spec chapter 31, update 2 chapter 6), in the
  fitting drawer (snippets/fitting-drawer.liquid). Four steps:
  1. items: the ones that came with the click, each with a size and an X;
     more from the wishlist, up to six; or "advice, without a specific item";
  2. time: fourteen days from tomorrow, closed days dimmed; half-hour slots
     from opening to an hour before closing; 11:00 and 16:00 are full;
  3. details: name, phone and a note, filled in for a signed-in visitor;
  4. summary, then "send".
  The request is saved in the demo layer as "pending approval". Closing the
  drawer midway keeps the choices and the step. A second request for the
  same time is refused with a link to the first. "Change time" goes back to
  step 2 and to "pending"; "cancel" makes it "cancelled".
  Every link to /pages/fitting opens the drawer in place with its parameters:
  product (+ variant or color), products (handle or handle:COLOR, comma
  separated), look, day (sunday..saturday). Storage goes only through
  window.DemoStore.
*/
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const MAX_ITEMS = 6;
  const DAYS = 14;
  const SLOT_MINUTES = 30;
  const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const store = () => window.DemoStore;

  /* -------------------------------------------------------------------------
     Products
  ------------------------------------------------------------------------- */
  const productCache = new Map();
  const loadProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  let indexPromise = null;
  const loadIndex = () => {
    indexPromise ||= fetch(`${root}collections/catalog?view=search-index`)
      .then((res) => res.json())
      .then((data) => data.products || [])
      .catch(() => []);
    return indexPromise;
  };

  const sized = (src, width) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${width}` : '');

  /** A row of step 1 from the product JSON, in the color (and size) that came with the click. */
  const toItem = (product, entry) => {
    const optionIndex = (name) => product.options.findIndex((option) => (option.name || option) === name);
    const colorIndex = optionIndex('Color');
    const sizeIndex = optionIndex('Size');
    let { color = null, size = null } = entry;
    if (entry.variantId) {
      const variant = product.variants.find((item) => String(item.id) === String(entry.variantId));
      if (variant) {
        if (colorIndex >= 0) color = variant.options[colorIndex];
        if (sizeIndex >= 0) size = variant.options[sizeIndex];
      }
    }
    const colors = colorIndex >= 0 ? product.options[colorIndex].values : [];
    if (colors.length && !colors.includes(color)) color = colors[0];
    if (!colors.length) color = null;
    const sizes = sizeIndex >= 0 ? product.options[sizeIndex].values : [];
    if (!sizes.includes(size)) size = null;
    const media = (product.media || []).find((item) => color && item.alt === color) || (product.media || [])[0];
    return {
      handle: product.handle,
      title: product.title,
      vendor: product.vendor,
      image: sized(media?.preview_image?.src || media?.src || product.featured_image, 240),
      color,
      size,
      sizes,
    };
  };

  /** What a click on a /pages/fitting link asks for. */
  const requestFrom = (url, trigger) => {
    const params = url.searchParams;
    const entries = [];
    if (params.get('product')) {
      entries.push({ handle: params.get('product'), color: params.get('color'), variantId: params.get('variant') });
    }
    (params.get('products') || '')
      .split(',')
      .filter(Boolean)
      .forEach((part) => {
        const [handle, color] = part.split(':');
        entries.push({ handle, color: color || null });
      });
    // From a product page or quick add: the color and size chosen there.
    const form = trigger?.closest?.('amor-product-form');
    if (form && entries[0]) {
      if (form.color) entries[0].color = form.color;
      if (form.size) entries[0].size = form.size;
    }
    return { entries, day: params.get('day') };
  };

  /* -------------------------------------------------------------------------
     Boutique hours: "10:00–20:00" per weekday, empty when closed.
  ------------------------------------------------------------------------- */
  const parseHours = (text) => {
    const match = String(text || '').match(/(\d{1,2}):(\d{2})\s*[–—-]\s*(\d{1,2}):(\d{2})/);
    if (!match) return null;
    return { open: Number(match[1]) * 60 + Number(match[2]), close: Number(match[3]) * 60 + Number(match[4]) };
  };

  const clock = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

  /* -------------------------------------------------------------------------
     <amor-fitting>
  ------------------------------------------------------------------------- */
  class AmorFitting extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('amor-drawer');
      this.strings = JSON.parse(this.querySelector('[data-fitting-strings]').textContent);
      this.hours = this.dataset.hours.split('|').map(parseHours);
      this.full = this.dataset.full.split(',');
      this.weekdays = this.strings.weekdays.split(',');
      this.weekdaysShort = this.strings.weekdaysShort.split(',');
      this.closeIcon = this.drawer.querySelector('[data-drawer-close] svg');

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-fitting-consult]')) {
          this.draft.consult = event.target.checked;
          this.querySelector('[data-fitting-items-error]').hidden = true;
          this.save();
        }
      });
      this.drawer.addEventListener('drawer:close', () => this.onClose());
    }

    fresh() {
      return { mode: 'new', step: 1, items: [], consult: false, date: null, time: null, name: '', phone: '', note: '' };
    }

    save() {
      if (this.draft && this.draft.mode === 'new' && this.draft.step !== 'done') store()?.setFittingDraft(this.draft);
    }

    /* --- Opening -------------------------------------------------------------- */

    async open(request = {}, trigger) {
      if (request.reschedule) {
        const saved = store()?.appointments().find((item) => item.id === request.reschedule);
        if (!saved) return;
        this.draft = {
          mode: 'reschedule',
          id: saved.id,
          step: 2,
          items: saved.items || [],
          consult: Boolean(saved.consult),
          date: saved.date,
          time: saved.time,
          name: saved.name || '',
          phone: saved.phone || '',
          note: saved.note || '',
        };
        this.doneRequest = null;
        if (!this.drawer.isOpen) this.drawer.open(trigger);
        this.render();
        return;
      }

      const saved = store()?.fittingDraft();
      this.draft = saved && saved.mode === 'new' ? saved : this.fresh();
      this.doneRequest = null;
      this.prefillDetails();
      this.drawer.open(trigger);
      this.render();

      if (request.entries?.length) {
        this.setAttribute('aria-busy', 'true');
        const items = await Promise.all(
          request.entries.map((entry) => loadProduct(entry.handle).then((product) => product && toItem(product, entry)))
        );
        this.removeAttribute('aria-busy');
        let added = false;
        items.filter(Boolean).forEach((item) => {
          const existing = this.draft.items.find((row) => row.handle === item.handle);
          if (existing) {
            existing.color = item.color;
            if (item.size) existing.size = item.size;
          } else if (this.draft.items.length < MAX_ITEMS) {
            this.draft.items.push(item);
            added = true;
          }
        });
        if (added) this.draft.step = 1;
      }

      const weekday = WEEKDAYS.indexOf(String(request.day || '').toLowerCase());
      if (weekday !== -1) {
        const date = this.dates().find((day) => day.date.getDay() === weekday && !day.closed);
        if (date && date.iso !== this.draft.date) {
          this.draft.date = date.iso;
          this.draft.time = null;
        }
      }

      this.save();
      this.render();
    }

    prefillDetails() {
      if (!store()?.signedIn()) return;
      const details = store().details();
      if (!this.draft.name) this.draft.name = details.name || '';
      if (!this.draft.phone) this.draft.phone = details.phone || '';
    }

    onClose() {
      if (this.draft?.step === 'done' || this.draft?.mode === 'reschedule') {
        this.draft = null;
        this.doneRequest = null;
      } else {
        this.save();
      }
    }

    /* --- Dates and times -------------------------------------------------------- */

    dates() {
      return Array.from({ length: DAYS }, (_, offset) => {
        const date = store().dateFromDays(offset + 1);
        return { date, iso: store().isoDate(date), closed: !this.hours[date.getDay()] };
      });
    }

    slots(iso) {
      const hours = this.hours[store().parseDate(iso).getDay()];
      if (!hours) return [];
      const times = [];
      for (let minutes = hours.open; minutes <= hours.close - 60; minutes += SLOT_MINUTES) times.push(clock(minutes));
      return times;
    }

    dateText(iso) {
      const date = store().parseDate(iso);
      return { day: this.weekdays[date.getDay()], date: `${date.getDate()}.${date.getMonth() + 1}` };
    }

    whenText(iso, time) {
      const { day, date } = this.dateText(iso);
      return this.strings.when.replace('__DAY__', day).replace('__DATE__', date).replace('__TIME__', time);
    }

    conflict() {
      const { date, time, id } = this.draft;
      if (!date || !time) return null;
      return store()
        .appointments()
        .find((item) => item.status !== 'cancelled' && item.date === date && item.time === time && item.id !== id);
    }

    /* --- Rendering ---------------------------------------------------------- */

    render() {
      if (!this.draft) return;
      const step = this.draft.step;
      this.querySelectorAll('[data-step]').forEach((section) => (section.hidden = section.dataset.step !== String(step)));
      this.querySelectorAll('[data-step-marker]').forEach((marker) => {
        const number = Number(marker.dataset.stepMarker);
        const current = number === step;
        marker.toggleAttribute('aria-current', current);
        if (current) marker.setAttribute('aria-current', 'step');
        marker.classList.toggle('is-done', step === 'done' || number < step);
      });

      const back = this.querySelector('[data-journey-back]');
      const next = this.querySelector('[data-journey-next]');
      back.hidden = step === 1 || step === 'done' || (this.draft.mode === 'reschedule' && step === 2);
      next.textContent = step === 4 ? this.strings.send : step === 'done' ? this.strings.close : this.strings.continue;

      if (step === 1) this.renderItems();
      if (step === 2) this.renderTimes();
      if (step === 3) this.renderDetails();
      if (step === 4) this.renderSummary(this.querySelector('[data-fitting-summary]'), true);
      if (step === 'done') this.renderDone();
    }

    goTo(step) {
      this.draft.step = step;
      this.save();
      this.render();
      this.querySelector('[data-journey-body]').scrollTop = 0;
      this.querySelector(`[data-step="${step}"] .journey__title`)?.focus({ preventScroll: true });
    }

    renderItems() {
      const list = this.querySelector('[data-fitting-items]');
      list.replaceChildren(...this.draft.items.map((item, index) => this.itemRow(item, index)));
      this.querySelector('[data-fitting-empty]').hidden = this.draft.items.length > 0;
      this.querySelector('[data-fitting-consult]').checked = this.draft.consult;
      this.querySelector('[data-fitting-max]').hidden = this.draft.items.length < MAX_ITEMS;
      this.renderWishlist();
    }

    itemRow(item, index) {
      const row = document.createElement('li');
      row.className = 'journey-item';
      row.classList.toggle('is-invalid', Boolean(item.invalid));

      const image = document.createElement('img');
      image.className = 'journey-item__thumb';
      image.alt = '';
      image.loading = 'lazy';
      if (item.image) image.src = item.image;

      const info = document.createElement('div');
      info.className = 'journey-item__info';
      const vendor = document.createElement('span');
      vendor.className = 'label';
      vendor.textContent = item.vendor;
      const title = document.createElement('p');
      title.className = 'journey-item__title';
      title.dir = 'ltr';
      title.textContent = item.title;
      info.append(vendor, title);
      if (item.color) {
        const color = document.createElement('p');
        color.className = 'journey-item__meta';
        color.textContent = `${this.strings.color}: ${item.color}`;
        info.append(color);
      }
      if (item.sizes?.length) {
        const sizes = document.createElement('div');
        sizes.className = 'pill-row journey-item__sizes';
        sizes.dir = 'ltr';
        sizes.setAttribute('role', 'group');
        sizes.setAttribute('aria-label', `${this.strings.size}: ${item.title}`);
        item.sizes.forEach((value) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'pill journey-item__size';
          pill.textContent = value;
          pill.setAttribute('aria-pressed', String(value === item.size));
          pill.dataset.fittingSize = `${index}|${value}`;
          sizes.append(pill);
        });
        info.append(sizes);
        const error = document.createElement('p');
        error.className = 'field-message field-message--error';
        error.setAttribute('role', 'status');
        error.textContent = this.strings.chooseSize;
        error.hidden = !item.invalid;
        info.append(error);
      } else {
        const one = document.createElement('p');
        one.className = 'journey-item__meta';
        one.textContent = this.strings.oneSize;
        info.append(one);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'icon-button journey-item__remove';
      remove.dataset.fittingRemove = String(index);
      remove.setAttribute('aria-label', this.strings.remove.replace('__TITLE__', item.title));
      if (this.closeIcon) remove.append(this.closeIcon.cloneNode(true));

      row.append(image, info, remove);
      return row;
    }

    async renderWishlist() {
      const box = this.querySelector('[data-fitting-wishlist]');
      const ids = store()?.wishlist() || [];
      if (!ids.length || this.draft.items.length >= MAX_ITEMS) {
        box.hidden = true;
        return;
      }
      const index = await loadIndex();
      const handles = this.draft.items.map((item) => item.handle);
      const products = ids
        .map((id) => index.find((product) => String(product.id) === String(id)))
        .filter((product) => product && !handles.includes(product.h));
      box.hidden = products.length === 0;
      const list = this.querySelector('[data-fitting-wishlist-list]');
      list.replaceChildren(
        ...products.map((product) => {
          const row = document.createElement('li');
          row.className = 'journey-pick';
          const image = document.createElement('img');
          image.className = 'journey-pick__thumb';
          image.alt = '';
          image.loading = 'lazy';
          if (product.img) image.src = product.img;
          const text = document.createElement('span');
          text.className = 'journey-pick__text';
          const vendor = document.createElement('span');
          vendor.className = 'label';
          vendor.textContent = product.v;
          const title = document.createElement('span');
          title.dir = 'ltr';
          title.textContent = product.t;
          text.append(vendor, title);
          const add = document.createElement('button');
          add.type = 'button';
          add.className = 'btn btn--outline btn--sm';
          add.dataset.fittingPick = product.h;
          add.setAttribute('aria-label', this.strings.add.replace('__TITLE__', product.t));
          const plus = document.createElement('span');
          plus.textContent = '+';
          add.append(plus);
          row.append(image, text, add);
          return row;
        })
      );
    }

    renderTimes() {
      const days = this.dates();
      if (!this.draft.date || !days.some((day) => day.iso === this.draft.date && !day.closed)) {
        this.draft.date = days.find((day) => !day.closed)?.iso || null;
        this.draft.time = null;
      }

      const strip = this.querySelector('[data-fitting-dates]');
      strip.replaceChildren(
        ...days.map((day) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'journey-date';
          button.dataset.fittingDate = day.iso;
          button.setAttribute('aria-pressed', String(day.iso === this.draft.date));
          const { day: weekday, date } = this.dateText(day.iso);
          button.setAttribute('aria-label', `${weekday}, ${date}${day.closed ? `, ${this.strings.closed}` : ''}`);
          if (day.closed) {
            button.disabled = true;
            button.classList.add('is-closed');
          }
          const short = document.createElement('span');
          short.className = 'journey-date__day';
          short.textContent = this.weekdaysShort[day.date.getDay()];
          const number = document.createElement('span');
          number.className = 'journey-date__number';
          number.textContent = date;
          button.append(short, number);
          return button;
        })
      );
      strip.querySelector('[aria-pressed="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });

      const dayLine = this.querySelector('[data-fitting-day]');
      if (this.draft.date) {
        const { day, date } = this.dateText(this.draft.date);
        dayLine.textContent = this.strings.dayLine.replace('__DAY__', day).replace('__DATE__', date);
      }

      const times = this.querySelector('[data-fitting-times]');
      times.replaceChildren(
        ...this.slots(this.draft.date).map((time) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'pill journey-time';
          pill.dataset.fittingTime = time;
          pill.setAttribute('aria-pressed', String(time === this.draft.time));
          const label = document.createElement('span');
          label.textContent = time;
          pill.append(label);
          if (this.full.includes(time)) {
            pill.disabled = true;
            pill.classList.add('is-full');
            const full = document.createElement('span');
            full.className = 'journey-time__full';
            full.textContent = this.strings.full;
            pill.append(full);
          }
          return pill;
        })
      );
      this.showConflict(false);
    }

    showConflict(show) {
      this.querySelector('[data-fitting-conflict]').hidden = !show;
    }

    renderDetails() {
      this.querySelector('[data-fitting-name]').value = this.draft.name || '';
      this.querySelector('[data-fitting-phone]').value = this.draft.phone || '';
      this.querySelector('[data-fitting-note]').value = this.draft.note || '';
    }

    /** The rows of the summary: items, time, place, details, note. */
    renderSummary(list, editable) {
      const rows = [];
      const itemLines = this.draft.items.map((item) =>
        [item.title, item.color, item.size].filter(Boolean).join(' · ')
      );
      if (this.draft.consult) itemLines.push(this.strings.consult);
      rows.push([this.strings.items, itemLines, 1]);
      rows.push([this.strings.time, [this.whenText(this.draft.date, this.draft.time)], 2]);
      rows.push([this.strings.place, [this.strings.boutique, this.dataset.address].filter(Boolean), null]);
      if (this.draft.mode === 'new') {
        rows.push([this.strings.contact, [this.draft.name, this.draft.phone].filter(Boolean), 3]);
        if (this.draft.note) rows.push([this.strings.note, [this.draft.note], 3]);
      }

      list.replaceChildren(
        ...rows.map(([term, lines, step]) => {
          const row = document.createElement('div');
          row.className = 'journey__summary-row';
          const dt = document.createElement('dt');
          dt.textContent = term;
          if (editable && step && (this.draft.mode === 'new' || step === 2)) {
            const change = document.createElement('button');
            change.type = 'button';
            change.className = 'link';
            change.dataset.fittingGoto = String(step);
            change.textContent = this.strings.change;
            dt.append(change);
          }
          const dd = document.createElement('dd');
          lines.forEach((line) => {
            const p = document.createElement('p');
            p.textContent = line;
            dd.append(p);
          });
          row.append(dt, dd);
          return row;
        })
      );
    }

    renderDone() {
      const request = this.doneRequest;
      if (!request) return;
      const cancelled = request.status === 'cancelled';
      this.querySelector('[data-fitting-done-title]').textContent = cancelled ? this.strings.cancelledTitle : this.strings.doneTitle;
      const status = this.querySelector('[data-fitting-status]');
      status.textContent = cancelled ? this.strings.statusCancelled : this.strings.statusPending;
      status.classList.toggle('is-cancelled', cancelled);
      const list = this.querySelector('[data-fitting-done-summary]');
      const items = request.items?.length ? this.strings.itemsCount.replace('__COUNT__', request.items.length) : null;
      const rows = [
        [this.strings.time, this.whenText(request.date, request.time)],
        [this.strings.items, [items, request.consult ? this.strings.consult : null].filter(Boolean).join(' · ')],
        [this.strings.place, this.strings.boutique],
      ];
      list.replaceChildren(
        ...rows.map(([term, text]) => {
          const row = document.createElement('div');
          row.className = 'journey__summary-row';
          const dt = document.createElement('dt');
          dt.textContent = term;
          const dd = document.createElement('dd');
          dd.textContent = text;
          row.append(dt, dd);
          return row;
        })
      );
      this.querySelector('[data-fitting-done-reschedule]').hidden = cancelled;
      this.querySelector('[data-fitting-done-cancel]').hidden = cancelled;
    }

    /* --- Steps ------------------------------------------------------------------ */

    validate(step) {
      if (step === 1) {
        let firstInvalid = null;
        this.draft.items.forEach((item, index) => {
          item.invalid = Boolean(item.sizes?.length && !item.size);
          if (item.invalid && firstInvalid === null) firstInvalid = index;
        });
        if (firstInvalid !== null) {
          this.renderItems();
          this.querySelectorAll('.journey-item')[firstInvalid]?.querySelector('.pill')?.focus();
          return false;
        }
        if (!this.draft.items.length && !this.draft.consult) {
          this.querySelector('[data-fitting-items-error]').hidden = false;
          return false;
        }
        return true;
      }
      if (step === 2) {
        const error = this.querySelector('[data-fitting-time-error]');
        const times = this.querySelector('[data-fitting-times]');
        if (!this.draft.time) {
          error.hidden = false;
          times.classList.add('is-invalid');
          times.querySelector('.pill:not(:disabled)')?.focus();
          return false;
        }
        if (this.conflict()) {
          this.showConflict(true);
          return false;
        }
        return true;
      }
      if (step === 3) {
        const nameOk = this.draft.name.trim().length > 0;
        const phoneOk = window.AmorAccount?.validPhone(this.draft.phone) ?? /^0\d{8,9}$/.test(this.draft.phone);
        this.fieldError('name', !nameOk);
        this.fieldError('phone', !phoneOk);
        if (!nameOk) this.querySelector('[data-fitting-name]').focus();
        else if (!phoneOk) this.querySelector('[data-fitting-phone]').focus();
        return nameOk && phoneOk;
      }
      return true;
    }

    fieldError(name, show) {
      const input = this.querySelector(`[data-fitting-${name}]`);
      input.closest('.field')?.classList.toggle('is-invalid', show);
      input.setAttribute('aria-invalid', String(show));
      this.querySelector(`[data-fitting-${name}-error]`).hidden = !show;
    }

    next() {
      const step = this.draft.step;
      if (step === 'done') {
        this.drawer.close();
        return;
      }
      if (!this.validate(step)) return;
      if (step === 4) {
        this.submit();
        return;
      }
      if (this.draft.mode === 'reschedule' && step === 2) {
        this.goTo(4);
        return;
      }
      this.goTo(step + 1);
    }

    back() {
      const step = this.draft.step;
      if (this.draft.mode === 'reschedule' && step === 4) this.goTo(2);
      else if (typeof step === 'number' && step > 1) this.goTo(step - 1);
    }

    submit() {
      const request = {
        items: this.draft.items.map(({ handle, title, vendor, image, color, size }) => ({ handle, title, vendor, image, color, size })),
        consult: this.draft.consult,
        date: this.draft.date,
        time: this.draft.time,
        name: this.draft.name.trim(),
        phone: this.draft.phone.trim(),
        note: this.draft.note.trim(),
      };
      if (this.draft.mode === 'reschedule') {
        this.doneRequest = store().updateAppointment(this.draft.id, { date: request.date, time: request.time, status: 'pending' });
      } else {
        this.doneRequest = store().addAppointment(request);
        store().setFittingDraft(null);
      }
      this.draft.step = 'done';
      this.render();
      this.querySelector('[data-fitting-done-title]').focus({ preventScroll: true });
    }

    /* --- Events ----------------------------------------------------------------- */

    async onClick(event) {
      const target = event.target;
      if (target.closest('[data-journey-next]')) return this.next();
      if (target.closest('[data-journey-back]')) return this.back();

      const size = target.closest('[data-fitting-size]');
      if (size) {
        const [index, value] = size.dataset.fittingSize.split('|');
        const item = this.draft.items[Number(index)];
        item.size = value;
        item.invalid = false;
        this.save();
        this.renderItems();
        this.querySelector(`[data-fitting-size="${index}|${CSS.escape(value)}"]`)?.focus();
        return;
      }

      const remove = target.closest('[data-fitting-remove]');
      if (remove) {
        this.draft.items.splice(Number(remove.dataset.fittingRemove), 1);
        this.save();
        this.renderItems();
        this.querySelector('[data-step="1"] .journey__title')?.focus();
        return;
      }

      const toggle = target.closest('[data-fitting-wishlist-toggle]');
      if (toggle) {
        const list = this.querySelector('[data-fitting-wishlist-list]');
        list.hidden = !list.hidden;
        toggle.setAttribute('aria-expanded', String(!list.hidden));
        return;
      }

      const pick = target.closest('[data-fitting-pick]');
      if (pick && this.draft.items.length < MAX_ITEMS) {
        pick.disabled = true;
        const product = await loadProduct(pick.dataset.fittingPick);
        if (product && !this.draft.items.some((item) => item.handle === product.handle)) {
          this.draft.items.push(toItem(product, {}));
          this.save();
        }
        this.renderItems();
        return;
      }

      const date = target.closest('[data-fitting-date]');
      if (date && !date.disabled) {
        this.draft.date = date.dataset.fittingDate;
        this.draft.time = null;
        this.save();
        this.renderTimes();
        this.querySelector(`[data-fitting-date="${this.draft.date}"]`)?.focus();
        return;
      }

      const time = target.closest('[data-fitting-time]');
      if (time && !time.disabled) {
        this.draft.time = time.dataset.fittingTime;
        this.save();
        this.querySelector('[data-fitting-time-error]').hidden = true;
        this.querySelector('[data-fitting-times]').classList.remove('is-invalid');
        this.querySelectorAll('[data-fitting-time]').forEach((pill) =>
          pill.setAttribute('aria-pressed', String(pill.dataset.fittingTime === this.draft.time))
        );
        this.showConflict(Boolean(this.conflict()));
        return;
      }

      const goto = target.closest('[data-fitting-goto]');
      if (goto) return this.goTo(Number(goto.dataset.fittingGoto));

      if (target.closest('[data-fitting-done-cancel]') && this.doneRequest) {
        this.doneRequest = store().updateAppointment(this.doneRequest.id, { status: 'cancelled' });
        this.renderDone();
        return;
      }
      if (target.closest('[data-fitting-done-reschedule]') && this.doneRequest) {
        this.open({ reschedule: this.doneRequest.id });
      }
    }

    onInput(event) {
      const target = event.target;
      if (target.matches('[data-fitting-name]')) {
        this.draft.name = target.value;
        this.fieldError('name', false);
      } else if (target.matches('[data-fitting-phone]')) {
        this.draft.phone = target.value;
        this.fieldError('phone', false);
      } else if (target.matches('[data-fitting-note]')) {
        this.draft.note = target.value;
      } else {
        return;
      }
      this.save();
    }
  }
  customElements.define('amor-fitting', AmorFitting);

  const fitting = () => document.querySelector('amor-fitting');

  const isFittingLink = (link) => {
    try {
      const url = new URL(link.href, window.location.href);
      return url.origin === window.location.origin && url.pathname.replace(/\/$/, '') === '/pages/fitting';
    } catch {
      return false;
    }
  };

  document.addEventListener('click', (event) => {
    const reschedule = event.target.closest('[data-fitting-reschedule]');
    if (reschedule && fitting()) {
      event.preventDefault();
      fitting().open({ reschedule: reschedule.dataset.fittingReschedule }, reschedule);
      return;
    }
    const opener = event.target.closest('[data-fitting-open]');
    const link = opener || event.target.closest('a[href]');
    if (!link || event.metaKey || event.ctrlKey || !fitting()) return;
    if (!opener && !isFittingLink(link)) return;
    event.preventDefault();
    const url = new URL(opener ? opener.dataset.fittingOpen || '/pages/fitting' : link.href, window.location.href);
    fitting().open(requestFrom(url, link), link);
  });

  // The page /pages/fitting opens the drawer at once, with the address's parameters.
  if (document.querySelector('[data-fitting-page]')) {
    customElements.whenDefined('amor-drawer').then(() => fitting()?.open(requestFrom(new URL(window.location.href))));
  }

  window.AmorFitting = { open: (request, trigger) => fitting()?.open(request, trigger) };
})();
